import { useEffect, useRef, useState, ReactElement } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import {
  IconAccount,
  IconBell,
  IconGlobe,
  IconInquiry,
  IconLogout,
  IconNavMypage,
  IconSetting,
  IconSignature,
  IconTerms,
  IconTrash,
  IconUserGuide,
  SupermetaLogo,
} from '@shared/ui/icons'
import { ConfirmModal } from '@shared/ui'
import { useAlarmList } from '@features/notification'
import { useAuthStore, useLogout } from '@features/auth'
import { useAccountList } from '@features/account'
import { useEmbeddedSignRequest } from '@features/sign'
import { NotificationPopup } from '@widgets/notification-list'

// 이메일 마스킹 — 로컬 파트 앞 3글자만 노출 (예: eun***@gmail.com)
const maskEmail = (email: string) => {
  const at = email.indexOf('@')
  if (at <= 0) return email
  const local = email.slice(0, at)
  const domain = email.slice(at)
  const visible = local.slice(0, 3)
  return `${visible}***${domain}`
}

const GNB_ITEMS = [
  { label: '거래하기', to: '/trade' },
  { label: '손익', to: '/pnl' },
] as const

interface UserMenuItem {
  label: string
  icon: () => ReactElement
  to?: string
  externalUrl?: string
  action?: 'embeddedSign' | 'logout'
}

const USER_GUIDE_URL = 'https://supermeta.gitbook.io/supermeta.trading'

const USER_MENU_ITEMS: UserMenuItem[] = [
  { label: '어카운트', icon: IconAccount, to: '/account' },
  { label: '비밀번호 변경', icon: IconSetting, to: '/password' },
  { label: '이용 가이드', icon: IconUserGuide, externalUrl: USER_GUIDE_URL },
  // { label: '비즈니스 문의', icon: IconInquiry, to: '/inquiry' },
  { label: '이용약관', icon: IconTerms, to: '/terms' },
  { label: '전자 서명', icon: IconSignature, action: 'embeddedSign' },
  { label: '회원탈퇴', icon: IconTrash, to: '/delete-user' },
  { label: '로그아웃', icon: IconLogout, action: 'logout' },
]

const UserMenuPopup = ({
  userId,
  accountLabel,
  items,
  onSelect,
}: {
  userId: string
  accountLabel: string
  items: UserMenuItem[]
  onSelect: (item: UserMenuItem) => void
}) => (
  <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-[226px] rounded-[8px] bg-[#262d36] py-3 shadow-[0px_4px_8px_rgba(0,0,0,0.18),0px_8px_18px_rgba(0,0,0,0.28)]">
    {/* User UID */}
    <div className="flex flex-col items-start justify-center border-b border-white/[0.18] px-4 pb-4">
      <div className="flex w-full items-center pb-1.5">
        <span className="font-pretendard text-[12px] font-normal tracking-[-0.3px] text-font-basic">
          {userId}
        </span>
      </div>
      <div className="flex items-center rounded-[8px] bg-black/[0.38] px-3 py-1.5">
        <span className="font-pretendard text-[12px] font-normal tracking-[-0.3px] text-font-gray">
          어카운트 : {accountLabel}
        </span>
      </div>
    </div>

    {/* 메뉴 항목 */}
    {items.map(item => {
      const Icon = item.icon
      return (
        <button
          key={item.label}
          type="button"
          onClick={() => onSelect(item)}
          className="flex w-full items-center border-b border-white/[0.18] px-6 last:border-b-0"
        >
          <div className="flex items-center py-4">
            <span className="flex items-center justify-center pr-2">
              <span className="flex h-3.5 w-3.5 items-center justify-center [&>svg]:h-full [&>svg]:w-full">
                <Icon />
              </span>
            </span>
            <span className="w-[130px] text-left font-pretendard text-sm font-semibold tracking-[-0.35px] text-font-gray">
              {item.label}
            </span>
          </div>
        </button>
      )
    })}
  </div>
)

export const PcHeader = () => {
  const navigate = useNavigate()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isNotiOpen, setIsNotiOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement | null>(null)
  const notiRef = useRef<HTMLDivElement | null>(null)
  const { alarms } = useAlarmList()
  const hasNotification = alarms.some(a => a.readStatus === 0)

  const isLoggedIn = useAuthStore(s => s.isLoggedIn)
  const userId = useAuthStore(s => s.userId)
  const { accounts, canShowEmbeddedSign } = useAccountList()
  const { logout } = useLogout()
  const { requestEmbeddedSign, isPending: isEmbeddedSignPending } = useEmbeddedSignRequest()
  const accountLabel = accounts[0]?.accountName ?? '-'
  const maskedEmail = userId ? maskEmail(userId) : '-'
  const userMenuItems = USER_MENU_ITEMS.filter(
    item => item.action !== 'embeddedSign' || canShowEmbeddedSign,
  )

  // 외부 클릭 시 메뉴 닫기 (퍼블리싱용 UX 처리)
  useEffect(() => {
    if (!isUserMenuOpen && !isNotiOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false)
      }
      if (notiRef.current && !notiRef.current.contains(e.target as Node)) {
        setIsNotiOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isUserMenuOpen, isNotiOpen])

  const handleEmailClick = () => {
    navigate('/account')
  }

  const handleUserIconClick = () => {
    setIsUserMenuOpen(prev => !prev)
  }

  const handleBellClick = () => {
    setIsNotiOpen(prev => !prev)
  }

  const handleNotiViewAll = () => {
    setIsNotiOpen(false)
    navigate('/notifications')
  }

  const handleUserMenuSelect = (item: UserMenuItem) => {
    setIsUserMenuOpen(false)
    if (item.action === 'logout') {
      // PC는 확인 없이 바로 로그아웃
      logout()
      return
    }
    if (item.action === 'embeddedSign') {
      if (isEmbeddedSignPending) return
      const signWindow = window.open('about:blank', '_blank')
      requestEmbeddedSign({ signWindow })
      return
    }
    if (item.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer')
      return
    }
    if (item.to) {
      navigate(item.to)
    }
  }

  return (
    <>
      <header className="relative z-50 flex h-[65px] w-full items-center border-b-4 border-bg-grey bg-[#1b1c1d] pb-1">
      <div className="flex h-full flex-1 items-center gap-4 px-4">
        {/* 로고 */}
        <Link to="/" className="flex items-center" aria-label="홈">
          <SupermetaLogo />
        </Link>

        {/* GNB */}
        <nav className="flex flex-1 items-center gap-6">
          {GNB_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `font-pretendard text-lg tracking-[-0.45px] ${
                  isActive ? 'font-semibold text-primary-yellow' : 'font-medium text-font-basic'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* 우측 — 로그인 여부로 분기 */}
        {isLoggedIn ? (
          /* 로그인: 이메일 + 모드태그 + 유저메뉴 + 알림 */
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4 pr-4">
              <button
                type="button"
                className="flex items-center justify-center rounded-[50px] bg-white/[0.08] px-6 py-2"
              >
                <span className="font-pretendard text-[12px] font-normal tracking-[-0.3px] text-font-basic">
                  {maskedEmail}
                </span>
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div ref={userMenuRef} className="relative">
                <button
                  type="button"
                  onClick={handleUserIconClick}
                  aria-label="계정 메뉴"
                  aria-expanded={isUserMenuOpen}
                  className="flex h-6 w-6 items-center justify-center"
                >
                  <IconNavMypage color="#efe9e9" />
                </button>
                {isUserMenuOpen && (
                  <UserMenuPopup
                    userId={userId || '-'}
                    accountLabel={accountLabel}
                    items={userMenuItems}
                    onSelect={handleUserMenuSelect}
                  />
                )}
              </div>
              <div
                ref={notiRef}
                className="relative"
                onMouseEnter={() => setIsNotiOpen(true)}
                onMouseLeave={() => setIsNotiOpen(false)}
              >
                <button
                  type="button"
                  onClick={handleBellClick}
                  aria-label="알림"
                  aria-expanded={isNotiOpen}
                  className="flex h-6 w-6 items-center justify-center"
                >
                  <IconBell hasNotification={hasNotification} />
                </button>
                {isNotiOpen && <NotificationPopup onViewAll={handleNotiViewAll} />}
              </div>
            </div>
          </div>
        ) : (
          /* 미로그인: 회원가입 + 로그인 + 언어 아이콘 */
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="flex items-center justify-center rounded-[50px] border border-font-basic px-6 py-2"
            >
              <span className="font-pretendard text-sm font-semibold tracking-[-0.35px] text-font-basic">
                회원가입
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="flex items-center justify-center rounded-[50px] bg-primary-yellow px-6 py-2"
            >
              <span className="font-pretendard text-sm font-semibold tracking-[-0.35px] text-[#1b1c1d]">
                로그인
              </span>
            </button>
            <button
              type="button"
              aria-label="언어 변경"
              className="flex h-6 w-6 items-center justify-center"
            >
              <IconGlobe />
            </button>
          </div>
        )}
      </div>
      </header>

    </>
  )
}
